/**
 * Production Records — school-admin NSLP/SBP compliance surface (PROD-1277,
 * redesigned in PROD-1279 part two).
 *
 * The page is a timeline of SERVICE DAYS with ONE card per campus-day
 * (`DayCard`): a compact row once complete (green check, mealtimes, who
 * signed, View details / Print) and an expanded card while open (progress
 * ring with the percent, one chip per part, ONE primary action that opens
 * the unified record). A single-campus school lists day cards. A
 * multi-campus school lists a header per day with one card per campus under
 * it, and a campus filter in the toolbar takes over from the campus tabs
 * that used to switch the whole page between campuses. The per-mealtime
 * record cards are gone too: "View details" opens the day's record page,
 * which shows the mealtimes as tabs.
 *
 * Layout (Sep 14 team review, kept): header (Compliance Docs, Print) → a
 * two-column body shared with Orders. The left column is the contained Road
 * Warrior shell (`EventTimelinePanel`) whose pinned toolbar carries the All /
 * Incomplete / Complete pill tabs on the left (Incomplete wears a red count
 * of the incomplete campus-days in the window) and the campus, date-window
 * and mealtime selects on the right; there is no alert above the list and no
 * status dropdown. The right rail is the Orders month calendar in day mode:
 * complete days green, incomplete past days red, days without a record muted
 * (on a multi-campus school a day is green only once every campus is
 * complete); clicking a day scrolls the list there, widening the window first
 * if the day is outside it. The page column fills the shell's main area and
 * the timeline is the only thing that scrolls — a page-level scrollbar
 * wrapped around a panel-level one is two ways to lose your place.
 *
 * Data: the `/days` rollup is per campus, so the page reads one window per
 * campus (`useQueries`) and merges client-side; the outstanding-days list —
 * the rule that actually pauses ordering — is read the same way for the
 * calendar. The Incomplete count, the calendar colours and the cards all
 * derive from the same lists and agree by construction.
 *
 * Finishing a record closes the dialog and returns to the list exactly where
 * the admin left it — the timeline, its filters and its scroll position sit
 * untouched underneath the dialog. Nothing opens on its own: a record is
 * entered only by clicking its card. The wizard header names the campus and
 * the service day the record is for (`taskContextLabel`).
 *
 * URL state: `?locationId=&task=record&day=yyyy-mm-dd[&mealtime=LUNCH]`
 * opens the unified production-record input (PROD-1279) on that campus-day
 * and, optionally, that mealtime step. It is the ONE way a day is filled from
 * this page (Abner, Sep 18): a card's action always opens it, and a legacy
 * `?task=transport-logs|service-records&day=yyyy-mm-dd` deep link (the Tasks
 * inbox and notifications still emit them) is REPLACED with `?task=record`
 * for that day on load, `mealtime` kept when the link carries one. A
 * single-campus school needs no `locationId`; a `locationId` alone (the
 * record page's back link) selects nothing, there being no tabs to select.
 *
 * Audience: `routes/profile.ts` resolves the "Production Records" nav item
 * for everyone with a reachable, provisioned campus they may work records
 * at — SCHOOL_ADMIN, CAFETERIA_WORKER, and chefs holding a records-level
 * cafeteria grant (PROD-1168) — the same set `/my-schools` returns. Cafeteria
 * mode has no link here; this page is the one records surface.
 */

import { EmptyState } from '@ordo/ui/empty-state'
import { EventTimelinePanel } from '@ordo/ui/event-timeline'
import { Skeleton } from '@ordo/ui/skeleton'
import { keepPreviousData, useQueries } from '@tanstack/react-query'
import { ClipboardCheck, ClipboardList } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import {
  fetchOutstandingDays,
  type Mealtime,
  outstandingDaysKey,
  type OutstandingDaysResponse,
  type OutstandingServiceDayWire,
} from '@/api/cafeteria'
import {
  daysVariablesForFilter,
  defaultFilterState,
  distinctAdminSchools,
  fetchProductionRecordDays,
  filterDays,
  type FilterState,
  type MySchoolLocation,
  productionRecordDaysKey,
  type ProductionRecordDaysResponse,
  type ProductionRecordDayWire,
  useMySchoolLocationsQuery,
  windowForDay,
} from '@/api/production-records'
import { useHasSchoolOrg } from '@/api/school-onboarding'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { MultiCampusTimeline, SingleCampusTimeline } from '@/components/production/day-timeline'
import { FilterRow } from '@/components/production/filter-row'
import { MobileDayTimeline, MobileRecordsLoading } from '@/components/production/mobile/mobile-day-timeline'
import { MobileFilterBar } from '@/components/production/mobile/mobile-filter-bar'
import { NutritionalAnalysisButton } from '@/components/production/nutritional-analysis-button'
import { ProductionCalendar } from '@/components/production/production-calendar'
import { RecordSpreadsheetDialog } from '@/components/production/spreadsheet/record-spreadsheet-dialog'
import { SchoolPrintButton } from '@/components/school-print/school-print-button'
import { SchoolPrintDialog } from '@/components/school-print/school-print-dialog'
import { SchoolSetupPendingEmptyState } from '@/components/school-setup-pending-empty-state'
import { usePhoneViewport } from '@/hooks/use-phone-viewport'
import { parseIsoDateParam } from '@/lib/cafeteria/format'
import { parseMealtimeParam } from '@/lib/mealtime'
import { attentionDayKeysAcrossCampuses, completeDayKeysAcrossCampuses } from '@/lib/production-records/calendar'
import {
  type CampusDays,
  dayProgress,
  groupCampusDays,
  incompleteCampusDays,
  selectedCampuses,
} from '@/lib/production-records/day-progress'
import { daySectionId, parseDayTaskParam } from '@/lib/production-records/day-tasks'
import { parseRecordTaskParam, RECORD_TASK_PARAM } from '@/lib/production-records/spreadsheet-mode'
import { todayKey } from '@/lib/school-orders/calendar'
import { NO_SCHOOLS_EMPTY, SupportEmailButton } from '@/lib/support'
import { productionDraftKey, useCafeteriaProductionDraftStore } from '@/stores/cafeteria-production-draft'
import { receiveDraftKey, useCafeteriaReceiveDraftStore } from '@/stores/cafeteria-receive-draft'

const NO_ROWS: ReadonlyArray<ProductionRecordDayWire> = []
const NO_OUTSTANDING: ReadonlyArray<OutstandingServiceDayWire> = []

export function ProductionRecordsPage() {
  const schoolsQuery = useMySchoolLocationsQuery()
  const schoolOrg = useHasSchoolOrg()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const schools = useMemo(() => distinctAdminSchools(schoolsQuery.data), [schoolsQuery.data])
  const [filterState, setFilterState] = useState<FilterState>(() => defaultFilterState())

  // `?locationId=` names the campus a task deep link (or a card's action) is
  // for. A single-campus school needs none: its one campus is implied. An
  // unknown id opens nothing.
  const requestedLocationId = searchParams.get('locationId')
  const taskCampus =
    schools.find((s) => s.id === requestedLocationId) ?? (schools.length === 1 ? (schools[0] ?? null) : null)

  // PROD-1279: a card action opens the unified production-record input
  // (`?task=record`); `?mealtime=` names the step inside it.
  const recordTask = parseRecordTaskParam(searchParams.get('task'))
  const legacyTask = parseDayTaskParam(searchParams.get('task'))
  const openDay = parseIsoDateParam(searchParams.get('day'))
  const openMealtime = parseMealtimeParam(searchParams.get('mealtime'))

  const setParams = useCallback(
    (mutate: (params: URLSearchParams) => void, options: { replace?: boolean } = {}) => {
      const params = new URLSearchParams(searchParams)
      mutate(params)
      setSearchParams(params, options)
    },
    [searchParams, setSearchParams],
  )

  // A legacy Tasks-inbox / notification deep link (`?task=transport-logs` or
  // `?task=service-records`) opens the unified record for that day: the URL is
  // REPLACED (no history entry for the old address) before anything mounts, so
  // the dialog mounts once, with `mealtime` kept when the link carries one.
  useEffect(() => {
    if (legacyTask && openDay) {
      setParams((params) => params.set('task', RECORD_TASK_PARAM), { replace: true })
    }
  }, [legacyTask, openDay, setParams])

  const openRecord = (locationId: string, serviceDate: string) =>
    setParams((params) => {
      params.set('locationId', locationId)
      params.set('task', RECORD_TASK_PARAM)
      params.set('day', serviceDate)
    })
  const closeTask = () =>
    setParams((params) => {
      params.delete('task')
      params.delete('day')
      params.delete('mealtime')
    })
  // Picking a step REPLACES the entry, so Back leaves the dialog rather than
  // walking back through every mealtime the admin looked at.
  const selectMealtime = (mealtime: Mealtime) =>
    setParams((params) => params.set('mealtime', mealtime), { replace: true })
  const viewRecord = (locationId: string, recordId: string) =>
    navigate(`/production/${encodeURIComponent(recordId)}?locationId=${encodeURIComponent(locationId)}`)

  if (schoolsQuery.isPending && !schoolsQuery.data) {
    return <PageLoading />
  }

  if (schoolsQuery.error) {
    return (
      <Container>
        <Header />
        <ErrorPanel message={schoolsQuery.error.message} />
      </Container>
    )
  }

  if (schools.length === 0) {
    return (
      <Container>
        <Header />
        {schoolOrg.hasSchoolOrg ? (
          <SchoolSetupPendingEmptyState surface="production" schoolName={schoolOrg.schoolName} />
        ) : (
          <EmptyState
            icon={<ClipboardCheck className="h-12 w-12" />}
            title={NO_SCHOOLS_EMPTY.title}
            description={NO_SCHOOLS_EMPTY.description}
            action={<SupportEmailButton subject="No schools linked — Production Records" />}
          />
        )}
      </Container>
    )
  }

  return (
    <Container>
      <Header schools={schools} />
      <RecordsBody
        schools={schools}
        filterState={filterState}
        onFilterChange={setFilterState}
        recordTask={recordTask}
        openDay={openDay}
        openMealtime={openMealtime}
        taskCampus={taskCampus}
        onOpenRecord={openRecord}
        onCloseTask={closeTask}
        onSelectMealtime={selectMealtime}
        onViewRecord={viewRecord}
      />
    </Container>
  )
}

// The panel pins the toolbar and scrolls the timeline inside itself (the
// Road Warrior pattern), taking whatever height the page column has left
// after the header — `Container` is a full-height flex column for exactly
// this, which is what lets the same page fit a single-campus school and a
// multi-campus one without a per-layout offset.
//
// `min-h-0` is load-bearing and there is deliberately no `min-h` FLOOR. A
// flex child defaults to `min-height: auto`, i.e. "never shrink below my
// content", so a long day list — or a 24rem floor on a short viewport —
// pushed the column past the page height and handed the page a scrollbar of
// its own, wrapped around the panel's. Two scrollbars for one list.
const PANEL_CLASS = 'min-h-0 flex-1'

// ---------------------------------------------------------------------------
// The body: day list + calendar rail + the print and record dialogs
// ---------------------------------------------------------------------------

interface RecordsBodyProps {
  /** Every campus the user oversees, in the order `/my-schools` lists them. Never empty here. */
  schools: ReadonlyArray<MySchoolLocation>
  filterState: FilterState
  onFilterChange: (next: FilterState) => void
  /** `?task=record` — the unified production-record input (PROD-1279), the one screen this page opens. */
  recordTask: 'record' | null
  openDay: string | null
  /** `?mealtime=` — the step inside the unified input, when the URL names one. */
  openMealtime: Mealtime | null
  /** The campus a `?task=record` deep link is for; null when the URL names none the user oversees. */
  taskCampus: MySchoolLocation | null
  onOpenRecord: (locationId: string, serviceDate: string) => void
  onCloseTask: () => void
  onSelectMealtime: (mealtime: Mealtime) => void
  onViewRecord: (locationId: string, recordId: string) => void
}

interface DaysQueryResult {
  data: ProductionRecordDaysResponse | undefined
  isPending: boolean
  isPlaceholderData: boolean
  error: Error | null
}

interface OutstandingQueryResult {
  data: OutstandingDaysResponse | undefined
}

/**
 * Owns the two reads the whole body hangs off — one `/days` window and one
 * outstanding-days list PER CAMPUS — because the Incomplete tab's count, the
 * calendar colours and the timeline all derive from the same lists and must
 * agree with each other.
 */
function RecordsBody({
  schools,
  filterState,
  onFilterChange,
  recordTask,
  openDay,
  openMealtime,
  taskCampus,
  onOpenRecord,
  onCloseTask,
  onSelectMealtime,
  onViewRecord,
}: RecordsBodyProps) {
  // Below `md` the body is a different tree (plan-06-mobile.md): the page
  // scrolls, the filters fold behind a button, and the timeline is the Road
  // Warrior card rail. Desktop and tablet render exactly what they always did.
  const phone = usePhoneViewport()
  // The window is the only server-side filter; memoized on the preset so the
  // `now`-anchored ISO strings do not re-key the queries on every render.
  const { dateWindow } = filterState
  const campusVars = useMemo(
    () => schools.map((campus) => ({ campus, vars: daysVariablesForFilter({ dateWindow }, campus.id) })),
    [schools, dateWindow],
  )
  const combineDays = useCallback(
    (results: DaysQueryResult[]) => ({
      pending: results.some((result) => result.isPending && !result.data),
      placeholder: results.some((result) => result.isPlaceholderData),
      error: results.find((result) => result.error)?.error ?? null,
      byCampus: results.map(
        (result, index): CampusDays => ({ campus: campusVars[index]!.campus, days: result.data?.days ?? NO_ROWS }),
      ),
    }),
    [campusVars],
  )
  const daysQueries = useQueries({
    queries: campusVars.map(({ vars }) => ({
      queryKey: productionRecordDaysKey(vars),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchProductionRecordDays(vars, signal),
      // Switching window re-keys the queries; keep the previous list on
      // screen (dimmed by the page) instead of flashing the skeleton.
      placeholderData: keepPreviousData,
      staleTime: 30 * 1000,
    })),
    combine: combineDays,
  })
  // Past days with open work beyond the loaded window: they colour the
  // calendar, and they agree with the rule that actually pauses ordering.
  const combineOutstanding = useCallback(
    (results: OutstandingQueryResult[]) =>
      new Map(results.map((result, index) => [schools[index]!.id, result.data?.days ?? NO_OUTSTANDING] as const)),
    [schools],
  )
  const outstandingByCampus = useQueries({
    queries: schools.map((campus) => ({
      queryKey: outstandingDaysKey({ schoolLocationId: campus.id }),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchOutstandingDays({ schoolLocationId: campus.id }, signal),
      staleTime: 30 * 1000,
    })),
    combine: combineOutstanding,
  })

  const receiveDrafts = useCafeteriaReceiveDraftStore((s) => s.drafts)
  const productionDrafts = useCafeteriaProductionDraftStore((s) => s.drafts)
  // "Save for later" drafts are persisted per truck (delivery date) and per
  // service day respectively — a draft turns Get started into Continue.
  const progressFor = useCallback(
    (locationId: string, day: ProductionRecordDayWire) =>
      dayProgress(day, {
        hasReceiveDraft: (deliveryDate) => receiveDraftKey(locationId, deliveryDate) in receiveDrafts,
        hasProductionDraft: productionDraftKey(locationId, day.serviceDate) in productionDrafts,
      }),
    [receiveDrafts, productionDrafts],
  )

  // -- The two layouts ---------------------------------------------------------
  const single = schools.length === 1
  const soleCampus = schools[0]!
  const { byCampus } = daysQueries
  const soleRows = single ? (byCampus[0]?.days ?? NO_ROWS) : NO_ROWS
  const singleDays = useMemo(() => filterDays(soleRows, filterState), [soleRows, filterState])
  const groups = useMemo(() => (single ? [] : groupCampusDays(byCampus, filterState)), [single, byCampus, filterState])
  // The phone groups a single campus by day too: the day header IS the
  // timeline there, so the card can carry the campus and its status instead.
  const phoneGroups = useMemo(
    () => (phone && single ? groupCampusDays(byCampus, filterState) : groups),
    [phone, single, byCampus, filterState, groups],
  )
  // The red count on the Incomplete tab: the campus-days that tab would list —
  // the loaded window's pending days after the mealtime (and campus) filters,
  // so the number always matches what a tap shows.
  const incompleteCount = useMemo(
    () =>
      single
        ? filterDays(soleRows, { status: 'pending', mealtimes: filterState.mealtimes }).length
        : incompleteCampusDays(byCampus, filterState),
    [single, soleRows, byCampus, filterState],
  )
  const empty = single ? singleDays.length === 0 : groups.length === 0

  // -- Calendar rail ----------------------------------------------------------
  const today = todayKey()
  const inScope = useMemo(() => selectedCampuses(byCampus, filterState.campuses), [byCampus, filterState.campuses])
  const completeDates = useMemo(() => completeDayKeysAcrossCampuses(inScope.map((entry) => entry.days)), [inScope])
  const attentionDates = useMemo(
    () =>
      attentionDayKeysAcrossCampuses(
        inScope.map((entry) => ({
          rows: entry.days,
          outstanding: outstandingByCampus.get(entry.campus.id) ?? NO_OUTSTANDING,
        })),
        today,
      ),
    [inScope, outstandingByCampus, today],
  )
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null)
  // A clicked day to scroll to once its section exists (the window may have
  // to widen and load first).
  const [scrollToDay, setScrollToDay] = useState<string | null>(null)
  const selectDay = (dayKey: string) => {
    setSelectedDayKey(dayKey)
    const window = windowForDay(dayKey, filterState.dateWindow)
    if (window && window !== filterState.dateWindow) {
      onFilterChange({ ...filterState, dateWindow: window })
    }
    // No preset reaches the day (older than 60 days): select it, nothing to jump to.
    setScrollToDay(window ? dayKey : null)
  }
  useEffect(() => {
    if (!scrollToDay || daysQueries.pending || daysQueries.placeholder) {
      return
    }
    if (!inScope.some((entry) => entry.days.some((row) => row.serviceDate === scrollToDay))) {
      // No service that day (on any campus in scope) — nothing to scroll to.
      setScrollToDay(null)
      return
    }
    const visible = single
      ? singleDays.some((day) => day.serviceDate === scrollToDay)
      : groups.some((group) => group.serviceDate === scrollToDay)
    if (!visible) {
      // Loaded but hidden by the status or mealtime filter: clear those so
      // the jump lands (this effect runs again once the list re-renders).
      onFilterChange({ ...filterState, status: 'all', mealtimes: new Set() })
      return
    }
    document.getElementById(daySectionId(scrollToDay))?.scrollIntoView({ block: 'start' })
    setScrollToDay(null)
  }, [
    scrollToDay,
    daysQueries.pending,
    daysQueries.placeholder,
    inScope,
    single,
    singleDays,
    groups,
    filterState,
    onFilterChange,
  ])

  // Per-card Print seeds the shared print dialog onto that campus-day. One
  // dialog for the whole page (not one per card): each mounted
  // `SchoolPrintButton` registers its own Cmd+P listener, so only the header
  // keeps a button.
  const [printTarget, setPrintTarget] = useState<{ locationId: string; serviceDate: string } | null>(null)
  const onPrint = useCallback(
    (locationId: string, serviceDate: string) => setPrintTarget({ locationId, serviceDate }),
    [],
  )

  const timeline = single ? (
    <SingleCampusTimeline
      campus={soleCampus}
      days={singleDays}
      progressFor={progressFor}
      onOpen={(locationId, day) => onOpenRecord(locationId, day.serviceDate)}
      onView={onViewRecord}
      onPrint={onPrint}
    />
  ) : (
    <MultiCampusTimeline
      groups={groups}
      progressFor={progressFor}
      onOpen={(locationId, day) => onOpenRecord(locationId, day.serviceDate)}
      onView={onViewRecord}
      onPrint={onPrint}
    />
  )

  if (phone) {
    return (
      // No panel, no calendar: the PAGE scrolls (the Road Warrior phone
      // layout), the filter bar sits on top, and the dialogs are the same
      // conditionally mounted ones the desktop branch renders below.
      <div className="space-y-4" data-testid="mobile-records-body">
        <MobileFilterBar
          state={filterState}
          onChange={onFilterChange}
          incompleteCount={incompleteCount}
          campuses={schools}
        />
        <DaysBody
          pending={daysQueries.pending}
          error={daysQueries.error}
          placeholder={daysQueries.placeholder}
          empty={empty}>
          <MobileDayTimeline
            groups={phoneGroups}
            multiCampus={!single}
            progressFor={progressFor}
            onOpen={(locationId, day) => onOpenRecord(locationId, day.serviceDate)}
            onView={onViewRecord}
            onPrint={onPrint}
          />
        </DaysBody>
        <SchoolPrintDialog
          key={printTarget ? `${printTarget.locationId}-${printTarget.serviceDate}` : 'closed'}
          open={printTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPrintTarget(null)
            }
          }}
          locationId={printTarget?.locationId ?? soleCampus.id}
          campuses={single ? undefined : schools}
          defaultSections={{ records: true, passout: false, orders: false }}
          initialDay={printTarget?.serviceDate}
        />
        {recordTask && openDay && taskCampus ? (
          <RecordSpreadsheetDialog
            key={`${taskCampus.id}-${openDay}-record`}
            locationId={taskCampus.id}
            locationName={taskCampus.name}
            serviceDate={openDay}
            mealtime={openMealtime}
            onSelectMealtime={onSelectMealtime}
            onClose={onCloseTask}
          />
        ) : null}
      </div>
    )
  }

  return (
    // The Orders body layout: the scrolling column and a fixed-width rail as
    // flex siblings (`week-orders-view.tsx`), so the two pages read as one
    // pattern. The rail is desktop-only, like Orders'.
    <div className="flex min-h-0 flex-1 gap-3">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        <EventTimelinePanel
          className={PANEL_CLASS}
          toolbar={
            <FilterRow
              state={filterState}
              onChange={onFilterChange}
              incompleteCount={incompleteCount}
              campuses={schools}
            />
          }>
          <DaysBody
            pending={daysQueries.pending}
            error={daysQueries.error}
            placeholder={daysQueries.placeholder}
            empty={empty}>
            {timeline}
          </DaysBody>
        </EventTimelinePanel>
      </div>
      <div className="hidden w-[240px] shrink-0 lg:block">
        <ProductionCalendar
          selectedDayKey={selectedDayKey}
          completeDates={completeDates}
          attentionDates={attentionDates}
          onSelectDay={selectDay}
        />
      </div>
      {/* Keyed on the campus-day: the dialog seeds its date range in a
          mount-time initializer, so a new per-card target must remount it. */}
      <SchoolPrintDialog
        key={printTarget ? `${printTarget.locationId}-${printTarget.serviceDate}` : 'closed'}
        open={printTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPrintTarget(null)
          }
        }}
        locationId={printTarget?.locationId ?? soleCampus.id}
        campuses={single ? undefined : schools}
        defaultSections={{ records: true, passout: false, orders: false }}
        initialDay={printTarget?.serviceDate}
      />
      {recordTask && openDay && taskCampus ? (
        <RecordSpreadsheetDialog
          key={`${taskCampus.id}-${openDay}-record`}
          locationId={taskCampus.id}
          locationName={taskCampus.name}
          serviceDate={openDay}
          mealtime={openMealtime}
          onSelectMealtime={onSelectMealtime}
          onClose={onCloseTask}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Day list states — loading, error, empty, or the loaded window (dimmed while
// the next window loads)
// ---------------------------------------------------------------------------

function DaysBody({
  pending,
  error,
  placeholder,
  empty,
  children,
}: {
  pending: boolean
  error: Error | null
  /** The previous window's list, shown dimmed while the new one loads. */
  placeholder: boolean
  empty: boolean
  children: ReactNode
}) {
  if (pending) {
    return <RecordsLoading />
  }
  if (error) {
    return <ErrorPanel message={error.message} />
  }
  if (empty) {
    return (
      <EmptyState
        icon={<ClipboardCheck className="h-12 w-12" />}
        title="No service days in this window"
        description="Days with deliveries or meal service appear here with their production records. Widen the date window or clear the filters above."
      />
    )
  }
  return (
    <div
      className={placeholder ? 'pointer-events-none opacity-60 transition-opacity' : 'transition-opacity'}
      aria-busy={placeholder || undefined}>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

function Container({ children }: { children: ReactNode }) {
  // OrdoPageContainer for the standard width + padding, as a full-height
  // flex column so the timeline panel (`flex-1`) fills whatever is left
  // under the header. The shell's <main> is a bounded scroll container, so
  // `h-full` resolves here the same way it does on the shopping list page.
  //
  // On a PHONE there is no panel: the list scrolls with the page, so the
  // column must be free to grow past the viewport (plan-06-mobile.md).
  const phone = usePhoneViewport()
  return (
    <OrdoPageContainer className={phone ? undefined : 'flex h-full min-h-0 flex-col'}>{children}</OrdoPageContainer>
  )
}

function Header({ schools = [] }: { schools?: ReadonlyArray<MySchoolLocation> }) {
  // Both header actions are per campus. A single campus is implied; a
  // multi-campus school picks the campus inside each dialog, starting on the
  // first one listed.
  const first = schools[0] ?? null
  const many = schools.length > 1
  return (
    <OrdoPageHeader
      icon={ClipboardList}
      title="Production Records"
      actions={
        <div className="flex items-center gap-2">
          <NutritionalAnalysisButton
            locationId={first?.id ?? null}
            schoolName={first?.name ?? null}
            campuses={many ? schools : undefined}
          />
          <SchoolPrintButton locationId={first?.id ?? null} campuses={many ? schools : undefined} />
        </div>
      }
    />
  )
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Couldn&rsquo;t load production records. {message}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeletons (mirror the real layout block-for-block)
// ---------------------------------------------------------------------------

function PageLoading() {
  const phone = usePhoneViewport()
  if (phone) {
    // The phone body: the tabs row with the Filters button, then the card
    // rail (`MobileRecordsLoading` mirrors the mobile card block-for-block).
    return (
      <Container>
        <Header />
        <div className="space-y-4" data-testid="mobile-records-loading-body">
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 flex-1 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-md" />
          </div>
          <MobileRecordsLoading />
        </div>
      </Container>
    )
  }
  // The two-column body: the panel on the left (pill tabs left, selects
  // right in its toolbar), the calendar rail on the right (desktop only,
  // like the real one).
  return (
    <Container>
      <Header />
      <div className="flex min-h-0 flex-1 gap-3">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <EventTimelinePanel
            className={PANEL_CLASS}
            toolbar={
              <>
                <Skeleton className="h-9 w-72 rounded-full" />
                <div className="ml-auto flex items-center gap-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="h-6 w-28 rounded-lg" />
                  ))}
                </div>
              </>
            }>
            <RecordsLoading />
          </EventTimelinePanel>
        </div>
        <div className="hidden w-[240px] shrink-0 lg:block">
          <CalendarLoading />
        </div>
      </div>
    </Container>
  )
}

function RecordsLoading() {
  // Mirrors the day card geometry: the status glyph, the title, the
  // mealtimes / signed line, the two actions on the right.
  return (
    <div className="space-y-2.5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3.5 rounded-lg border bg-card p-3.5">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-3.5 w-72" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Skeleton className="h-9 w-24 rounded-full" />
            <Skeleton className="h-9 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  )
}

function CalendarLoading() {
  // Mirrors `WeekMonthCalendar`: the month header between two paging
  // controls, the Mo–Fr header row, five rows of day circles, the legend.
  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-1">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
      <div className="grid grid-cols-5 justify-items-center gap-y-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={`h${i}`} className="my-1 h-3 w-5" />
        ))}
        {Array.from({ length: 25 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-8 rounded-full" />
        ))}
      </div>
      <div className="mt-2 flex justify-center gap-3">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}
